import React from 'react'
import { Modal } from '@/shared/ui/Modal'
import { Button, Checkbox, Input, Label, Radio } from '@/shared/ui'
import {
  insertMyRevshareBankAccount,
  setDefaultMyRevshareBankAccount,
  updateMyRevshareBankAccount,
  type RevshareBankAccount,
} from '../services/revshareBankService'

export default function RevshareBankAccountEditModal({
  open,
  onClose,
  value,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  value?: RevshareBankAccount | null
  onSaved?: () => void
}) {
  const isEdit = !!(value && value.id)
  const [method, setMethod] = React.useState<'pix' | 'ted'>(value?.method || 'pix')
  const [holder, setHolder] = React.useState(value?.holder_name || '')
  const [doc, setDoc] = React.useState(value?.document || '')
  const [pixType, setPixType] = React.useState(value?.pix_key_type || 'cpf')
  const [pixValue, setPixValue] = React.useState(value?.pix_key_value || '')
  const [bankCode, setBankCode] = React.useState(value?.bank_code || '')
  const [bankName, setBankName] = React.useState(value?.bank_name || '')
  const [branch, setBranch] = React.useState(value?.branch || '')
  const [accNum, setAccNum] = React.useState(value?.account_number || '')
  const [accType, setAccType] = React.useState(value?.account_type || '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [makeDefault, setMakeDefault] = React.useState(!!value?.is_default)

  React.useEffect(() => {
    if (!open) return
    setMethod(value?.method || 'pix')
    setHolder(value?.holder_name || '')
    setDoc(value?.document || '')
    setPixType(value?.pix_key_type || 'cpf')
    setPixValue(value?.pix_key_value || '')
    setBankCode(value?.bank_code || '')
    setBankName(value?.bank_name || '')
    setBranch(value?.branch || '')
    setAccNum(value?.account_number || '')
    setAccType(value?.account_type || '')
    setError(null)
    setMakeDefault(!!value?.is_default)
  }, [open, value?.id])

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      if (!holder.trim()) throw new Error('Informe o titular')
      if (method === 'pix' && !pixValue.trim()) throw new Error('Informe a chave PIX')
      if (method === 'ted' && (!bankCode.trim() || !branch.trim() || !accNum.trim())) {
        throw new Error('Preencha banco, agencia e conta')
      }
      const payload: RevshareBankAccount = {
        holder_name: holder.trim(),
        document: doc.trim() || null,
        method,
        pix_key_type: method === 'pix' ? (pixType as any) : null,
        pix_key_value: method === 'pix' ? pixValue.trim() : null,
        bank_code: method === 'ted' ? bankCode.trim() : null,
        bank_name: method === 'ted' ? bankName.trim() : null,
        branch: method === 'ted' ? branch.trim() : null,
        account_number: method === 'ted' ? accNum.trim() : null,
        account_type: method === 'ted' ? accType.trim() : null,
      }
      if (isEdit && value?.id) {
        await updateMyRevshareBankAccount(value.id, payload)
        if (makeDefault) await setDefaultMyRevshareBankAccount(value.id)
      } else {
        const id = await insertMyRevshareBankAccount(payload)
        if (makeDefault && id) await setDefaultMyRevshareBankAccount(id)
      }
      onClose()
      onSaved?.()
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()} className="p-6">
      <form className="space-y-4" onSubmit={onSave}>
        <div className="text-lg font-semibold">{isEdit ? 'Editar dados bancarios' : 'Adicionar dados bancarios'}</div>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
        <div className="space-y-2">
          <Label>Metodo</Label>
          <div className="flex items-center gap-6">
            <Radio name="method" checked={method === 'pix'} onChange={() => setMethod('pix')} label="PIX" />
            <Radio name="method" checked={method === 'ted'} onChange={() => setMethod('ted')} label="TED" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Titular</Label>
          <Input value={holder} onChange={(e) => setHolder(e.target.value)} placeholder="Nome completo" />
        </div>
        <div className="space-y-2">
          <Label>Documento (CPF/CNPJ)</Label>
          <Input value={doc} onChange={(e) => setDoc(e.target.value)} />
        </div>
        {method === 'pix' ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo da chave PIX</Label>
              <Input value={pixType} onChange={(e) => setPixType(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Chave PIX</Label>
              <Input value={pixValue} onChange={(e) => setPixValue(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Codigo do banco</Label>
              <Input value={bankCode} onChange={(e) => setBankCode(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Nome do banco</Label>
              <Input value={bankName} onChange={(e) => setBankName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Agencia</Label>
              <Input value={branch} onChange={(e) => setBranch(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Numero da conta</Label>
              <Input value={accNum} onChange={(e) => setAccNum(e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Tipo da conta</Label>
              <Input value={accType} onChange={(e) => setAccType(e.target.value)} placeholder="corrente / poupanca" />
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Checkbox checked={makeDefault} onChange={(e) => setMakeDefault((e.target as HTMLInputElement).checked)} />
          <span className="text-sm">Definir como conta padrao</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

